import dayjs from "utils/dayjs";

export function formatDate(timestamp: number): string | null {

  const formattedTimestamp = timestamp % 1 === 0 ? timestamp : timestamp * 1000;
  const date = new Date(formattedTimestamp);

  if (isNaN(date.getTime())) {
    return null;
  }

  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();

  return `${month}-${day}-${year}`;
}

export function formatDateDDMMMYYYY(timestamp: number): string | null {

  const formattedTimestamp = timestamp % 1 === 0 ? timestamp : timestamp * 1000;
  const date = new Date(formattedTimestamp);

  if (isNaN(date.getTime())) {
    return null;
  }

  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
}

export const formatDateToYYYYMMDD = (date: Date | null): string => {
  if (!date) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export function formatDateWithTime(dateString: string | number): string | null {
  const timestamp = typeof dateString === 'number'
    ? dateString
    : parseFloat(dateString);

  const formattedTimestamp = timestamp > 1e10 ? timestamp : timestamp * 1000;
  const date = new Date(formattedTimestamp);

  return isNaN(date.getTime())
    ? null
    : dayjs.utc(date).format("MMM-DD-YYYY, hh:mm A [UTC]");
}

export function parseJson(input: any) {
  try {
    return JSON.parse(input);
  } catch (e) {
    const fixedInput = input.replace(/'/g, '"');
    try {
      return JSON.parse(fixedInput);
    } catch (e) {
      throw new Error('Invalid JSON format');
    }
  }
}

/**
 * A calendar-date-shaped string: `YYYY-MM-DD…`. It can never be an epoch, because an
 * epoch has no hyphen in position 5.
 */
const ISO_DATE_LIKE = /^\d{4}-\d{2}-\d{2}/;

export function formatDateWithTimeDDMMMYYYY(dateString: string | number): string | null {
  /*
   * **ISO strings are handled FIRST, and silently were not.** The epoch path below runs
   * `parseFloat`, and `parseFloat('2026-09-17T12:45:52Z')` is **2026** — it stops at the
   * first hyphen. 2026 is then multiplied to 2,026,000 ms and the helper returns
   * `01-Jan-1970`: not `null`, not "Invalid Date", just a confidently wrong date that
   * passes every truthiness check a caller might write.
   *
   * Found while adding the UI Model list's "Last promoted" column — `last_promoted_at`
   * is the one timestamp the API sends as ISO 8601 while `createdAt` / `updatedAt` are
   * epoch floats. Fixed here rather than at that call site, because the next caller to
   * pass an ISO string would hit exactly the same thing.
   *
   * Backward compatible by construction: the branch is taken only for a string starting
   * `YYYY-MM-DD`, which no epoch can match, so every existing caller keeps its path.
   */
  if (typeof dateString === 'string' && ISO_DATE_LIKE.test(dateString)) {
    const isoDate = new Date(dateString);
    return isNaN(isoDate.getTime()) ? null : dayjs.utc(isoDate).format("DD-MMM-YYYY");
  }

  const timestamp = typeof dateString === 'number'
    ? dateString
    : parseFloat(dateString);

  const formattedTimestamp = timestamp > 1e10 ? timestamp : timestamp * 1000;
  const date = new Date(formattedTimestamp);

  return isNaN(date.getTime())
    ? null
    : dayjs.utc(date).format("DD-MMM-YYYY");
}